import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'

/**
 * `nextSyncAt` — the design gap-fill pass, 2026-09-17: the artboard's
 * "Sync frequency · Every 6 hours" describes nothing real (cadence is a
 * single global 900s webhook-driven interval, deliberately unsettable), and
 * the honest replacement is `last_synced_at` (already on `AccountResponse`)
 * plus this field. It has been on the wire the whole time
 * (`ItemSyncResponse.next_sync_at`, `schema.d.ts:4670`) and silently
 * survived the trip through `data` even before this type named it — the
 * function below returned `data` directly, and TypeScript's structural
 * typing does not strip properties a wider source object carries beyond
 * what the ANNOTATED return type declares. Mapped explicitly now, to a
 * `Date`, matching every other timestamp this module hands its caller
 * (`Account.lastSyncedAt`) rather than a wire string a component would
 * otherwise have to parse itself.
 */
export type ItemSyncOutcome = { status: string; message: string; skipped: boolean; nextSyncAt: Date | null }

/**
 * What "Sync now" reports — `ItemSyncResponse.status` is a bare `string` on
 * the wire (FX-9: not binary), and the two outcomes a caller can say anything
 * useful about without the SDK/reauth machinery Ticket 3 owns are "nothing to
 * do" and "asked, and it will arrive". Anything else falls back to the
 * server's own `message`, which is what a 502/`mfa_required` reads as here —
 * not wrong, just not specially worded.
 *
 * Moved here from `AccountDetail.tsx` (Task 5, #442) so `Connections.tsx`'s
 * own "Sync now" reads the SAME phrasing rather than growing a second copy
 * that could drift — a throttled sync should not say two different things on
 * two screens.
 */
export function summarizeSync(result: ItemSyncOutcome): string {
  if (result.skipped) return 'Already up to date.'
  if (result.status === 'pending') return 'Sync started — this can take a minute.'
  if (result.status === 'completed') return 'Synced.'
  return result.message
}

/**
 * `POST /aggregator/items/{item_id}/sync` — PRD-M5-R17 keeps "Sync now" as a
 * real settings action (narrowed by D-M5-2, not removed), and the endpoint
 * needs neither the Plaid SDK nor an OAuth redirect, so it is not behind the
 * same open questions (Q13-Q16) blocking Ticket 3's link/reauth funnels.
 *
 * **Deliberately narrow.** `ItemSyncResponse.status` is a bare `string` on
 * the wire (FX-9: "not binary") with real branches this hook does not
 * handle — `mfa_required` (relaunch the provider's Connect SDK) and `409
 * reauth_required` (open a reauth session) both need the dispatcher/adapter
 * machinery `providers/registry.ts` will own once Ticket 3 lands. Until
 * then those surface through the caller's generic failure toast rather than
 * their own guided flow — a defensible gap given the alternative today is no
 * "Sync now" at all, but Ticket 3 should absorb this hook rather than grow a
 * second one beside it.
 *
 * `user_present: true`, always — a click on "Sync now" is definitionally a
 * human at the screen asking for one, the same fact a pull-to-refresh
 * reports on mobile (`sync_item_api_v1_aggregator_items__item_id__sync_post`'s
 * own docblock).
 *
 * Invalidates every account rather than the one `itemId` belongs to: Ticket
 * 2 has no join from item to account id (`Account.itemId` is unread outside
 * `AccountDetail.tsx`'s own Disconnect/Sync controls today), so the narrower
 * invalidation Ticket 3's `useAggregator.ts` can do once it owns that join is
 * not available here.
 *
 * **Carries no analytics of its own** (B1, architect review, #399). An
 * earlier version threaded a telemetry-only `scope` parameter into this
 * TanStack mutation hook's public signature and emitted `sync_triggered`
 * once per `sync()` call — which fires once PER CONNECTION inside
 * `Accounts.tsx`'s "Sync all" fan-out, so five connections synced by one
 * press produced five events with no record that five was one press. The
 * fan-out width is the fact that mattered and it was never in the payload —
 * unrecoverable downstream, since the information had already left the
 * client. `sync_triggered` now fires once, at each call site's own point of
 * intent (`Accounts.tsx`'s `useSyncAll.run`, `AccountDetail.tsx`'s
 * `onSync`), which is also where "one press" is a fact this hook cannot see.
 */
export function useSyncItem(): {
  sync: (itemId: string) => Promise<ItemSyncOutcome>
  isPending: boolean
} {
  const client = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (itemId: string): Promise<ItemSyncOutcome> => {
      const { data, error, response } = await api.POST('/api/v1/aggregator/items/{item_id}/sync', {
        params: { path: { item_id: itemId }, query: { user_present: true } },
      })
      if (error !== undefined || data === undefined) {
        throw new Error(`Sync failed with ${response.status}`)
      }
      return {
        status: data.status,
        message: data.message,
        skipped: data.skipped,
        nextSyncAt: data.next_sync_at != null ? new Date(data.next_sync_at) : null,
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.accounts.all() })
    },
  })

  return { sync: (itemId) => mutation.mutateAsync(itemId), isPending: mutation.isPending }
}
